import React, { useState, useRef, useEffect, useMemo } from "react";
import "./customSelect.css"; // :point_left: external CSS file

export default function CustomSelect({
  name,
  options = [],
  placeholder = "Select",
  label,
  onChange,
  value,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const selectRef = useRef(null);
  // normalize options into { label, value } shape to support varying API shapes
  const normalized = useMemo(() => {
    return (Array.isArray(options) ? options : [])
      .map((opt) => {
        if (opt === null || opt === undefined) return null;
        if (typeof opt !== "object") return { label: String(opt), value: opt };
        // possible keys: label/value, id/statename, name/state, value
        const value =
          opt.value ??
          opt.id ??
          opt.stateid ??
          opt.countyid ??
          opt.county_id ??
          opt.countyId ??
          opt.state_id ??
          opt.stateId ??
          null;
        const label =
          opt.label ??
          opt.statename ??
          opt.name ??
          opt.state ??
          opt.countyname ??
          opt.county ??
          String(value ?? "");
        return { label, value };
      })
      .filter(Boolean);
  }, [options]);

  // keep internal selected in sync with external value prop
  // Handles both primitive values (IDs) and object values {label, value}
  useEffect(() => {
    if (value === undefined || value === null) {
      if (selected !== null) setSelected(null);
      return;
    }

    // If value is an object with a value property, extract the primitive value
    const primitiveValue = (typeof value === 'object' && value !== null && 'value' in value)
      ? value.value
      : value;

    const found = normalized.find((o) => o.value === primitiveValue);
    if (found) {
      if (!selected || selected.value !== found.value) {
        setSelected(found);
      }
    } else {
      if (selected !== null) setSelected(null);
    }
  }, [value, normalized, selected]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // keep highlighted/active item in sync when dropdown opens or selected changes
  useEffect(() => {
    if (!isOpen) return;
    const idx = normalized.findIndex((o) => (selected?.value || selected) === o.value);
    setActiveIndex(idx >= 0 ? idx : normalized.length ? 0 : -1);
  }, [isOpen, normalized, selected]);

  // scroll highlighted item into view when activeIndex changes
  useEffect(() => {
    if (activeIndex < 0) return;
    const items = selectRef.current?.querySelectorAll('.select-items li');
    if (items && items[activeIndex]) {
      items[activeIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        if (activeIndex >= 0) {
          const option = normalized[activeIndex];
          if (option) {
            setSelected(option);
            if (typeof onChange === 'function') {
              onChange(option.value, option);
            }
          }
        }
        setIsOpen(false);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      const next = Math.min((activeIndex === -1 ? 0 : activeIndex + 1), normalized.length - 1);
      setActiveIndex(next);
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      const prev = Math.max((activeIndex === -1 ? normalized.length - 1 : activeIndex - 1), 0);
      setActiveIndex(prev);
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      return;
    }
  };

  const selectId = name || `select-${Math.random()}`;

  return (
    <div className={`floating-select-wrapper ${label ? 'has-label' : ''}`}>
      <div
        className={`custom-select ${isOpen ? "open" : ""} ${disabled ? "disabled" : ""} ${selected ? 'has-value' : ''}`}
        ref={selectRef}
        name={name}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        {/* Selected Box */}
        <div
          className="select-selected"
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
          tabIndex={disabled ? -1 : 0}
        >
          {selected ? selected.label || selected : placeholder}
        </div>
        {/* Dropdown List */}
        <ul className="select-items" tabIndex={0}>
          {normalized.map((option, index) => (
            <li
              key={index}
              role="option"
              aria-selected={(selected?.value || selected) === option.value}
              tabIndex={-1}
              className={`${(selected?.value || selected) === option.value ? "selected" : ""} ${index === activeIndex ? 'highlighted' : ''}`.trim()}
              onClick={() => {
                 if (disabled) return;
                setSelected(option);
                setIsOpen(false);
                if (typeof onChange === "function") onChange(option.value, option);
              }}
            >
              {option.label}
            </li>
          ))}
        </ul>
      </div>
      {label && <label htmlFor={selectId}>{label}</label>}
    </div>
  );
}
//export default CustomSelect;
